import type { ComponentChildren } from 'preact';

import { cx } from '@amumin/ui-core';

import styles from './Card.module.css';

export interface CardMore {
  /** Hoeveel er niet getoond wordt (de tekst zelf, bv. "+3 meer", maakt de app). */
  label: string;
  href?: string | undefined;
  onPress?: (() => void) | undefined;
}

export interface CardProps {
  title?: string | undefined;
  /** Korte uitleg onder de titel (één zin). */
  subtitle?: string | undefined;
  children: ComponentChildren;
  /** Brede kaart (lijsten) i.p.v. de smalle formulierkaart van 440 px. */
  wide?: boolean | undefined;
  /**
   * Kop van de kaart: 1 = paginatitel (alleen een losse kaart zonder paginakop, zoals Inloggen);
   * 2 = kaartkop (lead 17/600, de tegelanatomie van het bord); 3 = idem als h3.
   */
  level?: 1 | 2 | 3 | undefined;
  /** Rechts in de kop: een telling ("4")... */
  count?: number | string | undefined;
  /** ...of één kleine actie (bv. "Toevoegen", "Alles"). */
  actions?: ComponentChildren | undefined;
  /** Onderaan: "+n meer" naar de lijstpagina of een blad (regel A1: hooguit 6 regels). */
  more?: CardMore | undefined;
  /** `none` voor lijsten die zelf tot de rand lopen. */
  padding?: 'normal' | 'none' | undefined;
  /** `quiet` = verzonken vlak zonder rand (secundaire info). */
  tone?: 'default' | 'quiet' | undefined;
  name?: string | undefined;
}

/**
 * Kaart (docs/ux/ontwerpsysteem.md): een vlak op de achtergrond met een haarlijn, geen schaduw,
 * ronding 16. Kop met titel en rechts een telling of één actie; onderaan eventueel "+n meer".
 * Eén niveau: binnen een kaart geen tweede kaart, alleen haarlijnen.
 */
export function Card({
  title,
  subtitle,
  children,
  wide,
  level = 1,
  count,
  actions,
  more,
  padding = 'normal',
  tone = 'default',
  name,
}: CardProps) {
  const className = cx(
    styles.card,
    wide && styles.wide,
    padding === 'none' && styles.flush,
    tone === 'quiet' && styles.quiet,
  );
  const heading = title ? (
    level === 1 ? (
      <h1 class={styles.title}>{title}</h1>
    ) : level === 3 ? (
      <h3 class={styles.title2}>{title}</h3>
    ) : (
      <h2 class={styles.title2}>{title}</h2>
    )
  ) : null;
  const hasCount = count !== undefined && count !== null && count !== '';
  return (
    <section class={className} data-name={name}>
      {heading || actions || hasCount ? (
        <div class={styles.head}>
          <div class={styles.headText}>
            {heading}
            {subtitle ? <p class={styles.subtitle}>{subtitle}</p> : null}
          </div>
          {hasCount ? <span class={cx(styles.count, 'tabular')}>{count}</span> : null}
          {actions ? <div class={styles.actions}>{actions}</div> : null}
        </div>
      ) : null}
      {children}
      {more ? (
        more.href ? (
          <a class={styles.more} href={more.href} data-name={name ? `${name}-more` : undefined}>
            {more.label}
          </a>
        ) : (
          <button
            type="button"
            class={styles.more}
            onClick={more.onPress}
            data-name={name ? `${name}-more` : undefined}
          >
            {more.label}
          </button>
        )
      ) : null}
    </section>
  );
}
